import {
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionPrimary,
  PageHeaderActions,
  VisibleFilterFields,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@yeshuman/sdk'
import { useState, useEffect, useRef } from 'react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { Stethoscope, Plus } from 'lucide-react'
import { Symptom, useSymptoms, useCreateSymptom, useUpdateSymptom, useDeleteSymptom, SymptomFilters, CreateSymptomData, UpdateSymptomData } from '../hooks/use-symptoms'
import { SymptomCard } from './symptom-card'
import { SymptomFormDialog } from './symptom-form-dialog'
import { SymptomStats } from './symptom-stats'
import { getHealthSymptomFilterDefinitions } from '../list-filters/symptoms'

const symptomFilterDefinitions = getHealthSymptomFilterDefinitions()
import { toast } from 'sonner'

export function SymptomsPage() {
  const [filters, setFilters] = useState<SymptomFilters>({})
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingSymptom, setEditingSymptom] = useState<Symptom | null>(null)
  const [deletingSymptomId, setDeletingSymptomId] = useState<string | null>(null)
  
  // Track previous symptoms for agent update animation
  const [highlightedSymptoms, setHighlightedSymptoms] = useState<Set<string>>(new Set())
  const previousSymptomsRef = useRef<Symptom[]>([])

  const { symptoms, isLoading, error, refetch } = useSymptoms(filters)
  const createSymptom = useCreateSymptom()
  const updateSymptom = useUpdateSymptom()
  const deleteSymptom = useDeleteSymptom()

  // Detect agent updates and highlight changed symptoms
  useEffect(() => {
    if (symptoms && previousSymptomsRef.current.length > 0) {
      const changedIds = new Set<string>()
      
      // Find new symptoms
      symptoms.forEach((symptom) => {
        const prev = previousSymptomsRef.current.find((s) => s.id === symptom.id)
        if (!prev) {
          changedIds.add(symptom.id)
        } else {
          // Check if symptom was updated (compare JSON strings)
          if (JSON.stringify(prev) !== JSON.stringify(symptom)) {
            changedIds.add(symptom.id)
          }
        }
      })
      
      if (changedIds.size > 0) {
        setHighlightedSymptoms(changedIds)
        // Fade out highlights after 3 seconds
        setTimeout(() => {
          setHighlightedSymptoms(new Set())
        }, 3000)
      }
    }
    
    // Update ref (doesn't trigger re-render)
    if (symptoms) {
      previousSymptomsRef.current = symptoms
    }
  }, [symptoms])

  const handleCreate = async (data: CreateSymptomData) => {
    try {
      await createSymptom.mutateAsync(data)
      toast.success('Symptom logged successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to create symptom')
    }
  }

  const handleUpdate = async (data: UpdateSymptomData) => {
    try {
      await updateSymptom.mutateAsync(data)
      toast.success('Symptom updated successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update symptom')
    }
  }

  const handleDelete = async (symptomId: string) => {
    try {
      await deleteSymptom.mutateAsync(symptomId)
      toast.success('Symptom deleted successfully')
      setDeletingSymptomId(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete symptom')
    }
  }

  const handleEdit = (symptom: Symptom) => {
    setEditingSymptom(symptom)
    setIsFormOpen(true)
  }

  const handleAdd = () => {
    setEditingSymptom(null)
    setIsFormOpen(true)
  }

  const handleFormClose = () => {
    setIsFormOpen(false)
    setEditingSymptom(null)
  }

  // Group symptoms by date
  const groupSymptomsByDate = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const weekAgo = new Date(today)
    weekAgo.setDate(weekAgo.getDate() - 7)

    const groups = {
      today: [] as Symptom[],
      yesterday: [] as Symptom[],
      thisWeek: [] as Symptom[],
      older: [] as Symptom[],
    }

    symptoms.forEach((symptom) => {
      const symptomDate = new Date(symptom.occurred_at)
      symptomDate.setHours(0, 0, 0, 0)

      if (symptomDate.getTime() === today.getTime()) {
        groups.today.push(symptom)
      } else if (symptomDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(symptom)
      } else if (symptomDate >= weekAgo) {
        groups.thisWeek.push(symptom)
      } else {
        groups.older.push(symptom)
      }
    })

    return groups
  }

  const groupedSymptoms = groupSymptomsByDate()

  const { title, description } = getPageHeader('/symptoms', 'Symptoms', 'Track and monitor your symptoms over time to identify patterns and trends.')
  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<Stethoscope className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary onClick={handleAdd}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Log Symptom
                </PageHeaderActionPrimary>
              }
            />
          }
        />

        {/* Statistics */}
        <SymptomStats symptoms={symptoms} />

        {/* Filters */}
        <VisibleFilterFields
          listId="healthSymptoms"
          filters={filters}
          onFiltersChange={setFilters}
          definitions={symptomFilterDefinitions}
          ctx={undefined}
        />

        {/* Error State */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">Failed to load symptoms: {error.message}</p>
              <div className="mt-4">
                <Button variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {isLoading ? <HealthListPageSkeleton showFilters={false} ariaLabel="Loading symptoms" /> : null}

        {/* Symptoms List */}
        {!isLoading && !error && (
          <div className="space-y-6">
            {symptoms.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Stethoscope className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No symptoms logged yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Start tracking your health symptoms by logging your first symptom.
                    </p>
                    <Button onClick={handleAdd}>
                      <Plus className="h-4 w-4 mr-2" />
                      Log Your First Symptom
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {groupedSymptoms.today.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Today</h2>
                    <div className="grid gap-4">
                      {groupedSymptoms.today.map((symptom) => (
                        <SymptomCard
                          key={symptom.id}
                          symptom={symptom}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingSymptomId(id)}
                          isHighlighted={highlightedSymptoms.has(symptom.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedSymptoms.yesterday.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Yesterday</h2>
                    <div className="grid gap-4">
                      {groupedSymptoms.yesterday.map((symptom) => (
                        <SymptomCard
                          key={symptom.id}
                          symptom={symptom}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingSymptomId(id)}
                          isHighlighted={highlightedSymptoms.has(symptom.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedSymptoms.thisWeek.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">This Week</h2>
                    <div className="grid gap-4">
                      {groupedSymptoms.thisWeek.map((symptom) => (
                        <SymptomCard
                          key={symptom.id}
                          symptom={symptom}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingSymptomId(id)}
                          isHighlighted={highlightedSymptoms.has(symptom.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedSymptoms.older.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Older</h2>
                    <div className="grid gap-4">
                      {groupedSymptoms.older.map((symptom) => (
                        <SymptomCard
                          key={symptom.id}
                          symptom={symptom}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingSymptomId(id)}
                          isHighlighted={highlightedSymptoms.has(symptom.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <SymptomFormDialog
          open={isFormOpen}
          onOpenChange={handleFormClose}
          symptom={editingSymptom}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingSymptomId} onOpenChange={(open) => !open && setDeletingSymptomId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete symptom</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this symptom? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deletingSymptomId && handleDelete(deletingSymptomId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
  )
}
